import React, {useEffect} from 'react'
import SearchBar from './SearchBar'
import SearchPropertyResults from '../search-property/SearchPropertyResults'
import {AppView, SearchParams} from '../../types'
import {useParallelSearchOffersQuery} from '../../hooks/useParallelSearchOffersQuery'
import {useAppDispatch, useAppSelector} from '../../store/hooks'
import {
  selectSearchParams,
  setSearchParams
} from '../../store/slices/searchParamsSlice'
import SearchUnitResults from '../search-unit/SearchUnitResults'
import {selectAppView, setAppView} from '../../store/slices/appViewSlice'
import Breadcrumb from '../common/Breadcrumb'
import {getLogger} from '../../services/logging'
import SearchHome from './SearchHome'
import {selectConfig} from '../../store/slices/configSlice'
import {
  selectSearchSubmitted,
  submitSearch
} from '../../store/slices/searchSubmittedSlice'
import {isAfter} from '../../services/dateUtils'
import {
  getPropertyConfigsForLocation,
  getPropertyConfigsForRegion,
  isRegionSearch
} from '../../services/utils'
import ExtendedStayBadge from '../common/ExtendedStayBadge'
import ExtendedStayModal from '../common/ExtendedStayModal'
import {pushDataLayerEvent} from '../../services/dataLayer'

const logger = getLogger('SearchView')

function shouldExecuteSearch(
  searchParams: SearchParams,
  searchSubmitted: Date | null,
  searchProcessed: Date | null
): boolean {
  if (!searchParams?.location || searchParams.location === 'ALL') {
    return false
  } else if (!searchSubmitted) {
    return false
  } else if (searchSubmitted && !searchProcessed) {
    return true
  } else if (searchSubmitted && searchProcessed) {
    return isAfter(searchSubmitted, searchProcessed)
  } else {
    return false
  }
}

const SearchView = () => {
  const [isExtendedStayModalOpen, setIsExtendedStayModalOpen] = React.useState(
    false
  )
  const dispatch = useAppDispatch()
  const appView = useAppSelector(selectAppView)
  const searchParams = useAppSelector(selectSearchParams)
  const searchSubmitted = useAppSelector(selectSearchSubmitted)
  const [searchProcessed, setSearchProcessed] = React.useState<Date | null>(
    null
  )
  const config = useAppSelector(selectConfig)
  const {
    offerResults,
    loading,
    triggerParallelFetch
  } = useParallelSearchOffersQuery()

  const isSearchRequested = shouldExecuteSearch(
    searchParams,
    searchSubmitted,
    searchProcessed
  )

  logger.info({
    message: `Checking if search should be executed: ${isSearchRequested}`,
    data: {
      searchParams,
      searchSubmitted,
      searchProcessed,
      isSearchRequested
    }
  })

  useEffect(() => {
    if (config && searchParams && isSearchRequested) {
      setSearchProcessed(searchSubmitted)

      const {dateRange, guests, specialRates} = searchParams

      // single property search
      if (searchParams.propertyId && searchParams.propertyId !== 'all') {
        const property = config.properties.find(
          property => property.propertyApaleoId === searchParams.propertyId
        )
        if (property) {
          const request = {
            propertyId: property.propertyApaleoId,
            dateRange,
            guests,
            specialRates
          }

          logger.info({
            message: 'Executing single property search...',
            data: {request, searchParams}
          })

          // show unit search results
          dispatch(setAppView(AppView.CHOOSE_UNIT))

          // trigger fetch for single property
          triggerParallelFetch([request])

          return
        }
      }

      // multiple properties search
      const properties = isRegionSearch(config, searchParams.location)
        ? getPropertyConfigsForRegion(config.properties, searchParams.location)
        : getPropertyConfigsForLocation(
            config.properties,
            searchParams.location
          )

      const requests = properties.map(property => {
        return {
          propertyId: property.propertyApaleoId,
          dateRange,
          guests,
          specialRates
        }
      })

      logger.info({
        message: 'Executing multiple property search...',
        data: {requests, searchParams}
      })

      // show property search results
      dispatch(setAppView(AppView.CHOOSE_PROPERTY))

      // trigger parallel fetch for all properties
      triggerParallelFetch(requests)
    }
  }, [config, searchParams, isSearchRequested])

  return (
    <>
      <section>
        <div className="flex flex-row gap-2 mb-2 items-center justify-end">
          <Breadcrumb className="flex-1" />
          <ExtendedStayBadge
            className="flex-none"
            onClick={() => {
              setIsExtendedStayModalOpen(true)

              pushDataLayerEvent({
                event: 'Extended_Stay_Info_View',
                category: 'IBE',
                value: 1
              })
            }}
          />
        </div>
        <SearchBar
          searchParams={searchParams}
          onSubmit={(updatedSearchParams: SearchParams) => {
            dispatch(setSearchParams(updatedSearchParams))
            dispatch(submitSearch())
          }}
          className="mb-8"
          config={config}
        />

        <div id="ibe-search-results">
          {appView === AppView.CHOOSE_PROPERTY && (
            <SearchPropertyResults
              searchParams={searchParams}
              offerResults={offerResults}
              isLoading={loading}
              onSelectProperty={(propertyId: string) => {
                dispatch(setSearchParams({...searchParams, propertyId}))
                dispatch(submitSearch())
              }}
            />
          )}

          {appView === AppView.CHOOSE_UNIT && (
            <SearchUnitResults
              offerResults={offerResults}
              selectedPropertyId={searchParams.propertyId || ''}
              isLoading={loading}
            />
          )}
        </div>
      </section>
      <ExtendedStayModal
        isOpen={isExtendedStayModalOpen}
        onClose={() => setIsExtendedStayModalOpen(false)}
      />
    </>
  )
}

export default SearchView
